import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { donationService } from '../../services';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import PageHeader from '../../components/common/PageHeader';

const MyReports = () => {
    const { i18n } = useTranslation();
    const lang = i18n.language;
    const [donations, setDonations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchDonations();
    }, []);

    const fetchDonations = async () => {
        try {
            setLoading(true);
            const response = await donationService.getMyDonations({ limit: 1000 }); // Get many to calculate stats
            setDonations(response?.data?.items || response?.items || []);
        } catch (err) {
            console.error('Error fetching donations:', err);
            setError('Failed to load your donation reports');
        } finally {
            setLoading(false);
        }
    };

    const successfulDonations = donations.filter(d => d.status === 'SUCCESS');
    const totalAmount = successfulDonations.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
    const gurukulDonations = successfulDonations.filter(d => d.type === 'CANDIDATE').length;
    const partyDonations = successfulDonations.filter(d => d.type === 'PARTY').length;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-primary-50 pb-8">
            <PageHeader
                title="My Contribution Report"
                subtitle="A detailed summary of your impact and support"
                icon={
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 00-4-4H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                }
            />
            <div className="max-w-4xl mx-auto px-4">

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <ReportCard title="Total Donated" value={formatCurrency(totalAmount, lang)} icon="💰" color="text-primary-600" />
                    <ReportCard title="Successful Donations" value={formatNumber(successfulDonations.length, lang)} icon="✅" color="text-green-600" />
                    <ReportCard title="Total Impact" value={`${formatNumber(gurukulDonations, lang)} Gurukuls`} icon="🎯" color="text-orange-600" />
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
                    <div className="p-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-6">Support Distribution</h3>
                        <div className="space-y-8">
                            <ProgressItem
                                label="Gurukul Support"
                                value={gurukulDonations}
                                total={successfulDonations.length}
                                color="bg-orange-500"
                                description="Direct contributions to Gurukul campaigns"
                            />
                            <ProgressItem
                                label="Gurukul Fund"
                                value={partyDonations}
                                total={successfulDonations.length}
                                color="bg-primary-600"
                                description="Contributions to the central party fund"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-primary-600 rounded-2xl p-8 text-white shadow-lg relative overflow-hidden">
                    <div className="relative z-10">
                        <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
                        <p className="text-primary-100 opacity-90 max-w-lg mb-0">
                            Your contributions are making a real difference in the political landscape of Nepal.
                            Together, we are building a more transparent and accountable future.
                        </p>
                    </div>
                    <div className="absolute top-0 right-0 -mt-10 -mr-10 opacity-10">
                        <svg className="w-64 h-64" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 10-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1a1 1 0 112 0v1a1 1 0 11-2 0zM13 16v-1a1 1 0 112 0v1a1 1 0 11-2 0zM14.243 14.243a1 1 0 101.414-1.414l-.707-.707a1 1 0 10-1.414 1.414l.707.707zM4.343 14.243a1 1 0 10-1.414 1.414l.707.707a1 1 0 001.414-1.414l-.707-.707z" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ReportCard = ({ title, value, icon, color }) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div className="text-3xl mb-4">{icon}</div>
        <div className="text-sm font-medium text-gray-500 mb-1">{title}</div>
        <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
);

const ProgressItem = ({ label, value, total, color, description }) => {
    const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <div>
            <div className="flex justify-between items-end mb-2">
                <div>
                    <span className="block text-sm font-bold text-gray-900">{label}</span>
                    <span className="text-xs text-gray-500">{description}</span>
                </div>
                <span className="text-sm font-bold text-gray-900">{percentage}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-3">
                <div
                    className={`${color} h-3 rounded-full transition-all duration-1000 ease-out`}
                    style={{ width: `${percentage}%` }}
                ></div>
            </div>
        </div>
    );
};

export default MyReports;
